import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AufgabenTyp, AufgabeVersandTaetigkeit } from '@schir-int-client/aufgabe-shared';
import { VerfuegungFacade } from '@schir-int-client/verfuegung-shared';
import { CreateAufgabeFormService } from '@schir-int-client/aufgabe';
import { filter, take } from 'rxjs/operators';
import { KontaktKategorie } from '@schir-int-client/adressverwaltung-shared';

@Injectable()
export class CreateVerfuegungFormService {
	private readonly facade = inject(VerfuegungFacade);
	private readonly aufgabeFormService = inject(CreateAufgabeFormService);

	readonly finanzsanktionslisteGroup = this.aufgabeFormService.form.controls.FINANZSANKTIONSLISTE;
	readonly schiffszertifikatGroup = this.aufgabeFormService.form.controls.SCHIFFSZERTIFIKAT;
	readonly schiffsbriefGroup = this.aufgabeFormService.form.controls.SCHIFFSBRIEF;
	readonly altesZertifikatGroup = this.aufgabeFormService.form.controls.ALTES_ZERTIFIKAT;
	readonly alterSchiffsbriefGroup = this.aufgabeFormService.form.controls.ALTER_SCHIFFSBRIEF;
	readonly zertifikatsauszugGroup = this.aufgabeFormService.form.controls.ZERTIFIKATSAUSZUG;
	readonly abschriftZertifikatsauszugGroup = this.aufgabeFormService.form.controls.ABSCHRIFT_ZERTIFIKATSAUSZUG;
	readonly alterAuszugGroup = this.aufgabeFormService.form.controls.ALTER_AUSZUG;
	readonly bescheinigungGroup = new FormGroup({
		bescheinigungsart: new FormControl<AufgabenTyp[]>([], Validators.required),
		notiz: new FormControl(''),
		versandTaetigkeit: new FormControl<AufgabeVersandTaetigkeit>(AufgabeVersandTaetigkeit.SENDEN_AN),
		empfaenger: new FormControl<KontaktKategorie[]>([], Validators.required),
		vorabPerFax: new FormControl(false),
		faxEmpfaenger: new FormControl<KontaktKategorie[]>([], Validators.required),
	});
	readonly registerabschriftGroup = this.aufgabeFormService.form.controls.REGISTERABSCHRIFT;
	readonly eintragungsmitteilungGroup = this.aufgabeFormService.form.controls.EINTRAGUNGSMITTEILUNG;
	readonly messbriefGroup = this.aufgabeFormService.form.controls.MESSBRIEF;
	readonly vermessungsurkundeGroup = this.aufgabeFormService.form.controls.VERMESSUNGSURKUNDE;
	readonly sonstigeGroup = this.aufgabeFormService.form.controls.SONSTIGE;
	readonly rechnungsstellungGroup = this.aufgabeFormService.form.controls.RECHNUNGSSTELLUNG;
	readonly kostenGroup = this.aufgabeFormService.form.controls.KOSTEN;
	readonly statistikGroup = this.aufgabeFormService.form.controls.STATISTIK;
	readonly heimathafenverlegungGroup = this.aufgabeFormService.form.controls.HEIMATHAFENVERLEGUNG;
	readonly heimatortverlegungGroup = this.aufgabeFormService.form.controls.HEIMATORTVERLEGUNG;
	readonly wiedervorlageGroup = this.aufgabeFormService.form.controls.WIEDERVORLAGE;
	readonly ablageGroup = this.aufgabeFormService.form.controls.ABLAGE;
	readonly controlVerfuegungNotiz = new FormControl<string>(null);

	readonly form = new FormGroup({ controlVerfuegungNotiz: this.controlVerfuegungNotiz });

	constructor() {
		this.bescheinigungGroup.disable();
	}

	submit(): boolean {
		if (this.checkPreconditions()) {
			const creating = this.facade.createVerfuegungByVorgang(this.controlVerfuegungNotiz.value);
			if (this.isAnyAufgabeSelected()) {
				creating
					.pipe(
						filter(created => !created.loading),
						take(1)
					)
					.subscribe(() => this.aufgabeFormService.submit());
			}
			return true;
		}
		return false;
	}

	checkPreconditions(): boolean {
		return (
			(!this.isAnyAufgabeSelected() || this.aufgabeFormService.isValid()) &&
			(!this.bescheinigungGroup.enabled || this.bescheinigungGroup.valid)
		);
	}

	private isAnyAufgabeSelected(): boolean {
		return (
			this.finanzsanktionslisteGroup.enabled ||
			this.schiffszertifikatGroup.enabled ||
			this.schiffsbriefGroup.enabled ||
			this.altesZertifikatGroup.enabled ||
			this.alterSchiffsbriefGroup.enabled ||
			this.zertifikatsauszugGroup.enabled ||
			this.alterAuszugGroup.enabled ||
			this.abschriftZertifikatsauszugGroup.enabled ||
			this.bescheinigungGroup.enabled ||
			this.registerabschriftGroup.enabled ||
			this.eintragungsmitteilungGroup.enabled ||
			this.messbriefGroup.enabled ||
			this.vermessungsurkundeGroup.enabled ||
			this.sonstigeGroup.enabled ||
			this.rechnungsstellungGroup.enabled ||
			this.kostenGroup.enabled ||
			this.statistikGroup.enabled ||
			this.heimathafenverlegungGroup.enabled ||
			this.heimatortverlegungGroup.enabled ||
			this.wiedervorlageGroup.enabled ||
			this.ablageGroup.enabled
		);
	}
}
